import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import differenceInHours from 'date-fns/differenceInHours';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import format from 'date-fns/format';
import datePickerLocaleEn from 'date-format-parse/lib/locale/en';
import datePickerLocaleEs from 'date-format-parse/lib/locale/es';
import datePickerLocalePtBR from 'date-format-parse/lib/locale/pt-br';

export const OPPORTUNITY_ACTIVITY_TYPES = [
  'call',
  'whatsapp',
  'online_meeting',
  'in_person_meeting',
  'store_visit',
  'test_drive',
  'email',
  'other',
];

export const OPPORTUNITY_ACTIVITY_STATUSES = ['open', 'completed', 'canceled'];

const SCHEDULE_KEY = 'OPPORTUNITY_DETAILS.ACTIVITIES.SCHEDULE';

const DATE_PICKER_LOCALES = {
  en: datePickerLocaleEn,
  es: datePickerLocaleEs,
  pt_BR: datePickerLocalePtBR,
};

export const activityTypeLabelKey = type =>
  `OPPORTUNITY_DETAILS.ACTIVITIES.TYPES.${String(
    type || 'other'
  ).toUpperCase()}`;

export const formatActivityHour = date =>
  date.getMinutes() ? format(date, "H'h'mm") : format(date, "H'h'");

export const formatActivityDateTime = value =>
  value ? format(new Date(value), 'dd/MM/yyyy HH:mm') : '-';

export const isPastDate = (value, now = new Date()) =>
  !!value && new Date(value) < now;

const pluralKey = (count, singular, plural) =>
  `${SCHEDULE_KEY}.${count === 1 ? singular : plural}`;

const overdueSchedule = (scheduledAt, now) => {
  const days = differenceInCalendarDays(now, scheduledAt);
  if (days >= 1) {
    return {
      key: pluralKey(days, 'OVERDUE_DAY', 'OVERDUE_DAYS'),
      params: { count: days },
    };
  }

  const hours = differenceInHours(now, scheduledAt);
  if (hours >= 1) {
    return {
      key: pluralKey(hours, 'OVERDUE_HOUR', 'OVERDUE_HOURS'),
      params: { count: hours },
    };
  }

  const minutes = Math.max(1, differenceInMinutes(now, scheduledAt));
  return {
    key: pluralKey(minutes, 'OVERDUE_MINUTE', 'OVERDUE_MINUTES'),
    params: { count: minutes },
  };
};

const upcomingSchedule = (scheduledAt, now) => {
  const days = differenceInCalendarDays(scheduledAt, now);
  const time = formatActivityHour(scheduledAt);

  if (days === 0) {
    return { tone: 'today', key: `${SCHEDULE_KEY}.TODAY`, params: { time } };
  }

  if (days === 1) {
    return {
      tone: 'future',
      key: `${SCHEDULE_KEY}.TOMORROW`,
      params: { time },
    };
  }

  return {
    tone: 'future',
    key: `${SCHEDULE_KEY}.IN_DAYS`,
    params: { count: days },
  };
};

export const activityScheduleInfo = (activity, now = new Date()) => {
  const scheduledAt = new Date(activity.scheduled_at);

  if (activity.status !== 'open') {
    return {
      tone: 'neutral',
      key: `${SCHEDULE_KEY}.ON_DATE`,
      params: {
        date: format(scheduledAt, 'dd/MM/yyyy'),
        time: format(scheduledAt, 'HH:mm'),
      },
    };
  }

  if (scheduledAt < now) {
    return { tone: 'overdue', ...overdueSchedule(scheduledAt, now) };
  }

  return upcomingSchedule(scheduledAt, now);
};

export const activitiesWithStatus = (activities, status) =>
  activities.filter(activity => activity.status === status);

export const activityDatePickerLang = locale => ({
  formatLocale: DATE_PICKER_LOCALES[locale] || datePickerLocalePtBR,
  yearFormat: 'YYYY',
  monthFormat: 'MMM',
  monthBeforeYear: true,
});
